<!--
  A context editor to use free contexts and followup contexts

  Props:
  - value: the value
  - intents: an array of available intents
      ex: [{
            value: "intentid",
            name: "intent_name",
            context: "intent_context" || null
          }, ...]
  - contexts: an array of existing contexts, as suggestions
      ex: ["context_name", ...]
  - intentid: the id of the current intent, used as default value for followup
  - disabled: to disable
  - placeholder: the placeholder string for empty context

  Emits:
  - input: input
  - rules: the rules to use to vaidate this context
  - update:followup: the status of the followup checkbox
-->

<template>
  <el-button-group>
    <!-- context type selection, as a "followup" checkbox -->
    <el-checkbox border
      @click.native.stop
      v-model="followup"
      :disabled="disabled">
      Followup
      <tooltip-icon placement="bottom">
        Select to choose a <em>followup context</em>
        instead of a normal <em>context</em>.<br/>
        <em>Followup contexts</em> are contexts
        automatically created by their parent <em>intent</em>.
      </tooltip-icon>
    </el-checkbox>
    <!-- the context autocomplete input -->
    <template v-if="!followup">
      <el-context-input
        :disabled="disabled"
        v-model="context"
        :placeholder="placeholder"
        :contexts="contexts">
      </el-context-input>
      <!-- a link to open the skill page at the right context -->
      <router-link
        style="padding: 12px"
        :to="{
          name: 'skill',
          params: {skill: $route.params.skill},
          query: $route.query,
          hash: context ? '#context-' + context : '#global-context',
        }"
        target="_blank"
        class="el-button"
        :tabindex="-1">
        <i class="el-icon-link"></i>
      </router-link>
    </template>
    <template v-else>
      <!-- the followup intent select -->
      <el-filterable-select
        v-model="intent"
        :disabled="disabled"
        placeholder="Choose an intent"
        :options="intentOptions"
        label-field="name">
        <template slot="option" slot-scope="{item}">
          <span v-if="item.context"
            class="followup-context-editor__prefix"
            >[{{item.contextName}}] </span>
          <span class="el-filterable-select__left"
            >{{ item.left }}</span>
          <strong class="el-filterable-select__match"
            >{{ item.match }}</strong>
          <span class="el-filterable-select__right"
            >{{ item.right }}</span>
        </template>
      </el-filterable-select>
      <!-- a link to open the intent page -->
      <router-link v-if="intent"
        style="padding: 12px"
        :to="{name: 'intent', params: {skill: $route.params.skill, intent: intent}, query: $route.query}"
        target="_blank"
        class="el-button"
        :tabindex="-1">
        <i class="el-icon-link"></i>
      </router-link>
      <el-button v-else disabled
        style="padding: 12px"
        :tabindex="-1">
        <i class="el-icon-link"></i>
      </el-button>
    </template>
  </el-button-group>
</template>

<script>
import { VariableValidator } from '../../utils/validators'
import ElContextInput from '../element/ElContextInput.vue'
import ElFilterableSelect from '../element/ElFilterableSelect.vue'

export default {
  name: 'ContextEditor',
  components: {
    ElContextInput,
    ElFilterableSelect,
  },
  props: {
    value: String,
    intents: Array,
    contexts: Array,
    intentid: String,
    disabled: Boolean,
    placeholder: String,
  },
  data () {
    // followup context validator
    const FollowupContextValidator = {
      validator: (rule, value, callback) => {
        if (!value || value.substr(0, 'followup:'.length) !== 'followup:') {
          return callback(new Error('invalid value'))
        }
        value = value.substr('followup:'.length)
        if (!value) return callback(new Error('intent required'))
        if (value === this.intentid) {
          return callback(new Error('cannot reference itself'))
        }
        return callback()
      },
      trigger: 'blur',
    }
    return {
      followup: null, // the followup checkbox
      context: '', // the context content
      intent: '', // the followup selector intentid
      VariableValidator,
      FollowupContextValidator,
      rules: [{
        // use VariableValidator for a free context
        ...VariableValidator,
        validator: (rule, value, callback) => {
          if (this.followup) return callback()
          return VariableValidator.validator(rule, value, callback)
        },
      }, {
        // use FollowupContextValidator for a followup context
        ...FollowupContextValidator,
        validator: (rule, value, callback) => {
          if (!this.followup) return callback()
          return FollowupContextValidator.validator(rule, value, callback)
        },
      }],
    }
  },
  computed: {
    /* the list of available intents, potentially with intentid prop */
    intentOptions () {
      if (!this.intentid) return this.intents
      return this.intents.filter(x => x.value !== this.intentid)
    },
    /* the result, by selecting context or followup field */
    input () {
      if (!this.followup) return this.context
      else if (this.intent) return 'followup:' + this.intent
      else return 'followup:'
    },
  },
  /* init values */
  created () {
    this.valueChanged()
    this.followupChanged()
    this.$emit('rules', this.rules)
  },
  watch: {
    /* check for a new value */
    value () { this.valueChanged() },
    /* emit new value */
    input () {
      if (this.followup !== null && this.input !== this.value) {
        this.$emit('input', this.input)
      }
    },
    /* check for followup checkbox change */
    followup () { this.followupChanged() },
  },
  methods: {
    /* update the checkbox and the related input */
    valueChanged () {
      if (this.value && this.value.substr(0, 'followup:'.length) === 'followup:') {
        this.followup = true
        this.intent = this.value.substr('followup:'.length)
      } else {
        this.followup = false
        this.context = this.value
      }
    },
    /* emit followup checkbox status */
    followupChanged () {
      if (this.followup !== null) this.$emit('update:followup', this.followup)
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .followup-context-editor__prefix {
    color: $--color-text-placeholder;
  }
</style>
